import React, { useEffect } from 'react';
import moment from 'moment';
import { I18n } from '@aws-amplify/core';
import { Row, Col, Checkbox, Space, Tooltip, message } from 'antd';
import type { TableRowSelection } from 'antd/es/table/interface';

import { Filter } from '../../Filter';
import { Container } from './Container';
import { Actions } from '../List/Actions/Actions';
import { FloatingBar } from '../List/FloatingActions';
import { Pagination } from '@/components/ui/Pagination';
import { useDataExtractionContext } from '@/context/DataExtraction';
import { useDataExtractionTable } from '../hooks/useDataExtractionTable';
import { useGetDataExtractionList } from '@/hooks/useGetDataExtractionList';
import { TableWrapper, TruncatedText } from './styles';
import { LabelMediumSemibold } from '@/components/Settings/DataExtraction/styles';

type TableDataItem = {
	status: string;
	id: string;
	content: string[];
	created_at: string;
	author_name: string;
	extraction_name: string;
	extraction_type: string;
	has_action_plans: boolean;
	organization_id: string;
	company_id: string;
};

export function TableList() {
	const { filter, handleOnSetPage, handleOnSetLimit } = useDataExtractionContext();
	const { isLoading, data, isError } = useGetDataExtractionList(filter);

	const {
		tableData,
		pagination,
		changePage,
		changeLimit,
		selectedIds,
		selectedRowKeys,
		isAllSelected,
		isIndeterminate,
		handleMainCheckboxChange,
		handleRowCheckboxChange,
		totalSelectedCount,
		triggerClearSelection
	} = useDataExtractionTable({ filters: filter });

	useEffect(() => {
		if (isError) {
			message.error(I18n.get('Error fetching data'));
		}
	}, [isError]);

	function handlePageChange(newPage: number) {
		handleOnSetPage(newPage);
		changePage(newPage);
	}

	function handleLimitChange(newLimit: number) {
		handleOnSetLimit(newLimit);
		changeLimit(newLimit);
	}

	const selectedRows = data?.rows?.filter((row) => selectedRowKeys.includes(row.id)) || [];

	function handleRowSelect(record: TableDataItem, selected: boolean) {
		handleRowCheckboxChange(record.id, selected);
	}

	function handleSelectAll(
		selected: boolean,
		_selectedRows?: readonly TableDataItem[],
		changeRows?: readonly TableDataItem[]
	) {
		if (changeRows) {
			const pageRowIds = changeRows.map((row) => row.id);
			handleRowCheckboxChange(pageRowIds.join(','), selected);
		}
	}

	function getRowClassName(record: TableDataItem) {
		if (!record || typeof record.id !== 'string') return '';
		return selectedIds.includes(record.id) ? 'selected-row' : '';
	}

	const columns: any = [
		{
			key: 'name',
			dataIndex: 'extraction_name',
			title: <LabelMediumSemibold>{I18n.get('Name')}</LabelMediumSemibold>,
			ellipsis: true,
			align: 'center',
			responsive: ['sm']
		},
		{
			key: 'content',
			dataIndex: 'content',
			title: <LabelMediumSemibold>{I18n.get('Content')}</LabelMediumSemibold>,
			ellipsis: true,
			align: 'center',
			responsive: ['xl'],
			render: (text: string[]) => <TruncatedText>{text.join(', ')}</TruncatedText>
		},
		{
			key: 'createdAt',
			dataIndex: 'created_at',
			title: <LabelMediumSemibold>{I18n.get('Date')}</LabelMediumSemibold>,
			ellipsis: true,
			align: 'center',
			responsive: ['sm'],
			render: (text: string) => (text ? moment(text).format('L') : '--/--/----')
		},
		{
			key: 'author',
			dataIndex: 'author_name',
			title: <LabelMediumSemibold>{I18n.get('Author')}</LabelMediumSemibold>,
			ellipsis: true,
			align: 'center',
			responsive: ['xl'],
			render: (text: string) => text ?? '-'
		},
		{
			key: 'status',
			title: <LabelMediumSemibold>{I18n.get('Status')}</LabelMediumSemibold>,
			dataIndex: 'status',
			ellipsis: true,
			align: 'center',
			responsive: ['lg'],
			render: (status: string) => status
		},
		{
			key: 'actions',
			title: <LabelMediumSemibold>{I18n.get('Actions')}</LabelMediumSemibold>,
			align: 'center',
			width: 160,
			render: (_: any, row: TableDataItem) => <Actions rowData={row} />
		}
	];

	const rowSelection: TableRowSelection<TableDataItem> = {
		selectedRowKeys,
		onSelect: handleRowSelect,
		onSelectAll: handleSelectAll,
		columnWidth: 60,
		columnTitle: (
			<Space onClick={(e) => e.stopPropagation()}>
				<Tooltip title={isAllSelected ? I18n.get('Unselect all') : I18n.get('Select all')}>
					<Checkbox
						checked={isAllSelected}
						indeterminate={isIndeterminate}
						onChange={handleMainCheckboxChange}
					/>
				</Tooltip>
			</Space>
		)
	};

	return (
		<>
			<Row gutter={[0, 16]}>
				<Col span={24}>
					<Filter />
				</Col>
				<Col span={24}>
					<Row justify="center">
						<Container>
							<TableWrapper
								rowKey="id"
								columns={columns}
								pagination={false}
								loading={isLoading}
								rowSelection={rowSelection}
								dataSource={data?.rows || []}
								rowClassName={getRowClassName}
							/>
						</Container>
					</Row>
					<FloatingBar
						totalSelectedCount={totalSelectedCount}
						onClearSelection={triggerClearSelection}
						selectedRowKeys={selectedRowKeys}
						selectedRows={selectedRows}
					/>
				</Col>
				<Col span={24}>
					<Pagination
						loading={isLoading}
						limit={pagination.limit}
						page={pagination.page}
						total={tableData.total}
						onSetLimit={handleLimitChange}
						onSetPage={handlePageChange}
					/>
				</Col>
			</Row>
		</>
	);
}
